import React, { useEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";
import MoonBackground from "./MoonBackground";
import CameraManager from "../../manager/CameraManager";
import useFlowStore from "../../store/flowStore";
import InteractUI from "../InteractUI/InteractUI";
import useCursorStore from "../../store/cursorStore";
import { audios, images, subtitles } from "../../../assets/assets";

export default function MoonUI({interactUIRef}) {
  const wrapperRef = useRef();
  const moonRef = useRef();
  const spotlightRef = useRef();
  const focusRef = useRef();
  const lastSoundStep = useRef(null);
  const hasTriggeredNextStep = useRef(false);
  const audioStartedRef = useRef(false);

  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const [moonFocused, setMoonFocused] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [spotlightVisible, setSpotlightVisible] = useState(true);
  const [focusPercent, setFocusPercent] = useState(0);
  const [closing, setClosing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [moonHovered, setMoonHovered] = useState(false); // Nouvel état
  const { setCursorScale } =
    useCursorStore.getState();

  const DEBUG = false;

  const {
    currentStep,
    flowSteps,
    playCurrentAudio,
    playAnimation,
    playAudioFile,
    playingAudio,
    nextStep,
  } = useFlowStore();

  const setVideoOverlayVisible = useFlowStore((s) => s.setVideoOverlayVisible);

  const step = flowSteps[currentStep];

  const moonPosition = useMemo(() => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    const centerX = screenWidth / 2;
    const centerY = screenHeight / 2;

    let top, left;
    const margin = 350; // distance minimum du centre

    do {
      top = Math.floor(Math.random() * (screenHeight - 100));
      left = Math.floor(Math.random() * (screenWidth - 100));
    } while (
      Math.abs(left - centerX) < margin &&
      Math.abs(top - centerY) < margin
    );

    return { top, left };
  }, []);

  useEffect(() => {
    if (wrapperRef.current) {
      gsap.to(wrapperRef.current, {
        opacity: 1,
        duration: 1,
        ease: "power2.out",
        onComplete: () => {
          playAudioFile(audios.debut_telescope, subtitles.debut_telescope);
        }
      });
    }
  }, []);

  // 🟡 Spotlight effect
  useEffect(() => {
    const handleMouseMove = ({ clientX: x, clientY: y }) => {
      setCoords({ x, y });
      if (!moonRef.current || moonFocused) return;
      
      const rect = moonRef.current.getBoundingClientRect();

      if (
        x >= rect.left &&
        x <= rect.right &&
        y >= rect.top &&
        y <= rect.bottom
      ) {
        if (!moonHovered) {
          setMoonHovered(true);
          setCursorScale(2.5);
          // Ne déclenche plus automatiquement le focus
        }
      } else {
        setMoonHovered(false);
        setCursorScale(1);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [moonFocused, moonHovered]);

  // Ajoutez un gestionnaire de clic :
  const handleMoonClick = () => {
    console.log("🌙 Clic sur la lune détecté !", { moonHovered, moonFocused });
    
    if (moonHovered && !moonFocused) {
      setMoonFocused(true);
      focusMoon();

      if (!hasTriggeredNextStep.current) {
        hasTriggeredNextStep.current = true;
        playCurrentAudio(() => {
          nextStep();
        });
      }
    }
  };

  // 🌀 Drag logic
  useEffect(() => {
    if (!moonFocused || !focusRef.current) return;

    let isDragging = false;
    let lastAngle = null;
    let center = { x: 0, y: 0 };

    const getCenter = () => {
      const svg = document.querySelector(".moonUI__progressSvg");
      const rect = svg.getBoundingClientRect();
      return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
    };

    const getAngleFromCenter = (x, y) => {
      const dx = x - center.x;
      const dy = y - center.y;
      return Math.atan2(dy, dx) * (180 / Math.PI);
    };

    const handleMouseDown = (e) => {
      e.preventDefault();
      isDragging = true;
      center = getCenter(); // 🔥 On calcule le vrai centre à chaque drag
      lastAngle = getAngleFromCenter(e.clientX, e.clientY);
      setIsDragging(true);
    };

    const handleMouseMove = (e) => {
      if (!isDragging || playingAudio) return;
      const currentAngle = getAngleFromCenter(e.clientX, e.clientY);
      let delta = currentAngle - lastAngle;

      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;

      lastAngle = currentAngle;

      setFocusPercent((prev) => Math.min(100, Math.max(0, prev + delta * 0.25)));
    };

    const handleMouseUp = () => {
      isDragging = false;
      lastAngle = null;
      setIsDragging(false);
    };

    const el = focusRef.current;
    el.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      el.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [moonFocused, playingAudio]);

  // 🔁 Validation + blur dynamique
  useEffect(() => {
    if (!moonRef.current) return;

    const inRange = focusPercent >= 70 && focusPercent <= 74;
    const blurValue = inRange
      ? 0
      : Math.min(5, Math.abs(focusPercent - 72) * 0.2);
    moonRef.current.style.filter = `blur(${blurValue}px)`;

    let timeoutId = null;

    if (inRange && !audioStartedRef.current) {
      timeoutId = setTimeout(() => {
        if (!audioStartedRef.current) {
          audioStartedRef.current = true;

          new Audio(audios.lune_trouvee)
            .play()
            .catch(console.warn);


          playCurrentAudio(() => {
            startClosing();
          });
        }
      }, 800);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [focusPercent]);

  // 🔘 Orbit position
  const center = { x: 275, y: 275 };
  const radius = 260;
  const angleDeg = -45 + (focusPercent / 100) * 180;
  const angleRad = (angleDeg * Math.PI) / 180;
  const x = center.x + radius * Math.cos(angleRad);
  const y = center.y + radius * Math.sin(angleRad);

  const focusMoon = () => {
    if (!moonRef.current) return;

    setMoonFocused(true);
    setShowIntro(false);
    setCursorScale(1);

    gsap.to(spotlightRef.current, {
      opacity: 0,
      duration: 1,
      ease: "power2.inOut",
      onComplete: () => setSpotlightVisible(false),
    });

    gsap.set(moonRef.current, { zIndex: 6 });
    gsap.to(moonRef.current, {
      top: "50%",
      left: "50%",
      x: "-50%",
      y: "-50%",
      scale: 3,
      position: "fixed",
      duration: 2,
      ease: "power4.inOut",
    });
  };

  const startClosing = () => {
    setClosing(true);
    // HIDE MOON UI
    setVideoOverlayVisible(true);

    gsap.to(wrapperRef.current, {
      opacity: 0,
      duration: 1,
      ease: "power2.inOut",
      onComplete: async () => {
        if (interactUIRef?.current) {
          await interactUIRef.current.exit();
        }
        
        console.log("🌙 MoonUI closing...");

        const swooshAudio = new Audio(audios.swoosh_telescope_out);
        swooshAudio.play().catch(console.warn);
        
        setTimeout(() => {
          CameraManager.reset();
          CameraManager.switchCamera("camera_5");
          playAudioFile(
            audios.timelapse,
            subtitles.timelapse
          );
        }, 12000);
      },
    });
  };

  // Joue un son tous les 5% de focus
  useEffect(() => {
    const step = Math.floor(focusPercent / 5);
    if (lastSoundStep.current !== step) {
      lastSoundStep.current = step;
      if (focusPercent > 0 && focusPercent < 100) {
        new Audio(audios.focal_simple_clic)
          .play()
          .catch(console.warn);
      }
    }
  }, [focusPercent]);

  return (
    <div
      className="moonUI"
      ref={wrapperRef}
      style={{
        pointerEvents: closing ? "none" : "auto",
      }}
    >
      <InteractUI
        ref={interactUIRef}
        label={step.interact?.label}
        order={step.interact?.order}
        totalSteps={step.interact?.totalSteps}
        indication={step.interact?.indication}
      />
      <MoonBackground />
      <div className="moonUI__maskedLayer" style={{ pointerEvents: 'none' }}>
        <img
          ref={moonRef}
          src={images.moonImg}
          alt="moon"
          className={`moonUI__moon ${moonHovered ? 'moonUI__moon--hovered' : ''}`}
          style={{
            top: `${moonPosition.top}px`,
            left: `${moonPosition.left}px`,
            pointerEvents: 'auto', // Force les événements sur l'image
          }}
          onClick={handleMoonClick}
          onMouseEnter={() => console.log("🌙 Mouse enter")}
          onMouseLeave={() => console.log("🌙 Mouse leave")}
        />
      </div>

      {spotlightVisible && (
        <div
          className="moonUI__spotlight"
          ref={spotlightRef}
          style={{ "--mouse-x": `${coords.x}px`, "--mouse-y": `${coords.y}px` }}
        />
      )}

      {moonFocused && (
        <>
          <div
            className="moonUI__vignette"
            style={{ "--focus-x": "50%", "--focus-y": "50%" }}
          />
          <div className="moonUI__progressWrapper">
            <svg className="moonUI__progressSvg" viewBox="0 0 300 300">
              <circle
                className="moonUI__progressDashed"
                cx="150"
                cy="150"
                r="140"
              />
              {DEBUG && (
                <>
                  <circle cx="150" cy="150" r="4" fill="red" />
                  <line
                    x1="150"
                    y1="150"
                    x2={150 + 140 * Math.cos(angleRad)}
                    y2={150 + 140 * Math.sin(angleRad)}
                    stroke="red"
                    strokeDasharray="5 3"
                  />
                </>
              )}
            </svg>
            <div
              className="moonUI__percentOrbit"
              style={{
                transform: `rotate(${angleDeg}deg)`,
                pointerEvents: "auto",
              }}
            >
              <div
                ref={focusRef}
                className={`moonUI__focusPercent ${isDragging ? "is-grabbing" : ""}`}
                style={{
                  top: `${y}px`,
                  left: `${x}px`,
                  transform: `
                    translate(-50%, -50%) 
                    rotate(${-angleDeg}deg) 
                    scale(${isDragging ? 1.3 : 1})`,
                  userSelect: "none",
                  zIndex: 9999,
                  pointerEvents: "auto",
                }}
              >
                {Math.round(focusPercent)}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
